import { useState } from "react";
import api from "../../../api/resources";
import {
  Setting,
  SettingDisplay,
  SettingList,
  SettingLocker,
  SettingModder,
  SettingSwitcher,
} from "../Common/Settings";
import { FormLoader, Input, useFormErrors } from "../../commons/Form/Form";
import { useAuth } from "../../../auth/AuthContext";
import { Navigate, useNavigate } from "react-router-dom";
import routes from "../../../routes/routes";
import { useToastsDispatch } from "../../commons/Toasts/ToastsContext";
import { useTranslation } from "react-i18next";
import Button from "../../commons/Button/Button";
import { Form } from "../../commons/Form/Form";
import { useModalDispatch } from "../../commons/Modal/ModalContext";
import Address from "../../commons/Address/Address";
import { formatDate } from "../../../format";
import { i18n } from "../../../i18n";

export function Company() {
  const { t } = useTranslation();
  document.title = t("settings:tabs.company.title");
  const dispatchToast = useToastsDispatch();
  const { user, loadingUser } = useAuth();

  const keys = [
    "name",
    "code",
    "activity",
    "immatriculation_date",
    "is_ecommerce",
    "address",
  ];

  const { activeCompany, loadingActiveCompany } = useAuth();

  const [company, setCompany] = useState(activeCompany);

  const handleEcomChange = async (value) => {
    const response = await api.updateActiveCompany({ is_ecommerce: value });

    if (!response.ok) {
      dispatchToast({
        type: "add",
        variant: "info",
        heading: "Info",
        subheading: "cannot modify e-commerce status",
      });
      return;
    }

    handleCompanyChange(value, "is_ecommerce");
  };

  const handleCompanyChange = (value, field) => {
    setCompany((prevCompany) => ({
      ...prevCompany,
      [field]: value,
    }));
  };

  const renderComponent = (key, { label, value }) => {
    const map = {
      name: (
        <SettingModder
          modal={{
            component: <CompanyNameForm label={label} initValue={value} />,
            onSuccess: () => window.location.reload(),
          }}
        />
      ),
      is_ecommerce: (
        <SettingSwitcher value={value} onSwitch={handleEcomChange} />
      ),
      address: (
        <SettingModder
          modal={{
            component: <AddressForm label={label} initValue={value} />,
            onSuccess: () => window.location.reload(),
          }}
        />
      ),
    };

    return map[key] || <SettingLocker />;
  };

  if (loadingActiveCompany || loadingUser) return <FormLoader />;
  return (
    <div>
      <SettingList>
        {keys.map((key) => {
          const label = (key) => {
            const map = {
              is_ecommerce: t("common:ecom"),
              activity: t("common:company.activity_code." + company.country),
              code: t("common:company.identifier." + company.country),
              address: t("common:address.label"),
            };

            return map[key] || t("common:" + key);
          };

          const displayValue = (key) => {
            const map = {
              address: (
                <Address address={company.address} country={company.country} />
              ),
              immatriculation_date: formatDate(
                company.immatriculation_date,
                i18n.resolvedLanguage,
              ),
              is_ecommerce: t(
                "common:" + (company.is_ecommerce ? "yes" : "no"),
              ),
            };

            return map[key] || company[key];
          };

          return (
            <Setting key={key} testid={key}>
              <SettingDisplay
                label={label(key)}
                value={displayValue(key)}
                testid={key}
              />
              {renderComponent(key, {
                label: label(key),
                value: company[key],
              })}
            </Setting>
          );
        })}
      </SettingList>

      {!company.as_reseller && (
        <div style={{ marginTop: "1rem" }}>
          <CompanyManage
            companyName={company.name}
            type={
              company.managers.length === 1
                ? "delete"
                : user.is_admin
                  ? "transfer"
                  : "quit"
            }
          />
        </div>
      )}
    </div>
  );
}

export function CompanyRedirect({ children }) {
  const { activeCompany, loadingActiveCompany } = useAuth();

  if (loadingActiveCompany) return <></>;
  if (!activeCompany) return <Navigate to={routes.Company.root} />;
  return children;
}

function CompanyManage({ companyName, type }) {
  const { t } = useTranslation(["common", "settings"]);
  const dispatchModal = useModalDispatch();
  const navigate = useNavigate();
  return (
    <div>
      <Button
        id="delete-company"
        variant="link"
        text={t(`settings:items.${type}Company.label`)}
        onClick={
          type === "transfer"
            ? () => navigate(routes.Settings.users)
            : () =>
                dispatchModal({
                  type: "add",
                  ...{
                    title: companyName,
                    message: t(`settings:modals.${type}Company.message`),
                    component: <ManageCompanyForm type={type} />,
                    onSuccess: () => window.location.reload(),
                  },
                })
        }
      />
    </div>
  );
}

function ManageCompanyForm({ type }) {
  const dispatchModal = useModalDispatch();
  const { t } = useTranslation();

  const formErrorKey = "non_field_errors";
  const { errors, setErrors, clearErrors } = useFormErrors([]);

  const handleDeleteCompany = async () => {
    clearErrors();
    const response = await api.deleteActiveCompany();
    if (!response.ok) {
      setErrors({
        [formErrorKey]: ["Une erreur s'est produite."],
      });
      return;
    }
    dispatchModal({ type: "success" });
  };

  return (
    <Form
      onSubmit={handleDeleteCompany}
      errors={errors[formErrorKey]}
      button={t(`common:${type}`)}
    />
  );
}

function CompanyNameForm({ label, initValue }) {
  const [name, setName] = useState(initValue);

  const dispatchModal = useModalDispatch();

  const formErrorKey = "non_field_errors";
  const inputKeys = ["name"];
  const { errors, setErrors, clearErrors } = useFormErrors(
    inputKeys,
    formErrorKey,
  );

  const handleSubmit = async () => {
    clearErrors();
    const response = await api.updateActiveCompany({ name });
    if (!response.ok) {
      const apiErrors = await response.json();
      setErrors(apiErrors);
      return;
    }
    dispatchModal({ type: "success" });
  };

  const input = {
    label,
    name: "name",
    value: name,
    onChange: setName,
    errors: errors.name,
  };

  const form = {
    onSubmit: handleSubmit,
    errors: errors[formErrorKey],
  };

  return (
    <Form {...form}>
      <Input {...input} />
    </Form>
  );
}

function AddressForm({ label, initValue }) {
  const { t } = useTranslation("common", "settings");
  const dispatchModal = useModalDispatch();

  const [address, setAddress] = useState(initValue);

  const formErrorKey = "non_field_errors";
  const inputKeys = ["line_1", "line_2", "code", "town"];
  const { errors, setErrors, clearErrors } = useFormErrors(inputKeys);

  const handleAddressChange = (value, addressField) => {
    setAddress((prevAddress) => ({
      ...prevAddress,
      [addressField]: value,
    }));
  };

  const handleSubmit = async () => {
    clearErrors();
    const response = await api.updateActiveCompany({ address });
    if (!response.ok) {
      const apiErrors = await response.json();
      if (apiErrors.address) {
        setErrors(apiErrors.address);
      }
      return;
    }
    dispatchModal({ type: "success" });
  };

  const inputs = inputKeys.map((key) => ({
    label: key === "line_1" ? label : "",
    placeholder: t("common:address." + key),
    name: key,
    value: address[key],
    onChange: (value) => handleAddressChange(value, key),
    errors: errors[key],
    required: !(key === "line_2"),
  }));

  const form = {
    onSubmit: handleSubmit,
    errors: errors[formErrorKey],
  };

  return (
    <Form {...form}>
      {inputs.map((input) => (
        <Input key={input.name} {...input} />
      ))}
    </Form>
  );
}
